<template>
  <div class="store-page">
    <section class="page-hero">
      <div class="shell">
        <nav class="breadcrumbs" aria-label="Хлебные крошки">
          <NuxtLink to="/">Главная</NuxtLink><span>/</span><span>Контакты</span>
        </nav>
        <span class="eyebrow">{{
          isPartnership ? 'WHITE LABEL · СОТРУДНИЧЕСТВО' : 'НАЧНЁМ С ЗНАКОМСТВА'
        }}</span>
        <h1 v-if="isPartnership">
          Создадим решение<br /><span class="gradient-text">под вашим брендом</span>
        </h1>
        <h1 v-else>Расскажите,<br /><span class="gradient-text">что для вас важно</span></h1>
        <p>
          Новый объект, существующие камеры или вопрос об оборудовании. Подготовим решение под вашу
          задачу.
        </p>
      </div>
    </section>
    <div class="shell page-section contact-layout">
      <div class="contact-info">
        <span class="eyebrow light-eyebrow">ROBOTICS VISION</span>
        <h2>Рядом с вашим<br />пространством</h2>
        <p>Санкт-Петербург<br />и Ленинградская область</p>
        <a v-if="phone" :href="phoneHref" class="contact-channel">{{ phoneDisplay }}</a
        ><a v-if="email" :href="`mailto:${email}`" class="contact-channel">{{ email }}</a>
        <div class="contact-preparation">
          <h3>Что поможет с расчётом</h3>
          <ul class="check-list">
            <li>Тип объекта и его площадь</li>
            <li>План помещений или участка</li>
            <li>Нужные зоны наблюдения</li>
            <li>Есть ли камеры и интернет</li>
          </ul>
        </div>
      </div>
      <form class="site-main-card contact-form" @submit.prevent="sendRequest">
        <h2>{{ isPartnership ? 'Обсудим сотрудничество' : 'Ваш проект начинается здесь' }}</h2>
        <div class="form-grid">
          <label class="input-field"
            >Как к вам обращаться<input
              v-model="name"
              name="name"
              autocomplete="name"
              maxlength="100"
              placeholder="Ваше имя" /></label
          ><label class="input-field"
            >Телефон или e-mail<input
              v-model="contact"
              name="contact"
              required
              maxlength="150"
              placeholder="Как с вами связаться"
          /></label>
        </div>
        <label class="input-field"
          >{{ isPartnership ? 'Расскажите о компании и проектах' : 'Что нужно сделать?'
          }}<textarea
            v-model="message"
            name="message"
            required
            maxlength="5000"
            :placeholder="
              isPartnership
                ? 'Название компании, какие объекты обслуживаете и сколько камер планируете подключать.'
                : 'Например: установить камеры в магазине, нужен обзор входа и кассы.'
            "
          />
        </label>
        <details v-if="attachment" class="request-attachment">
          <summary>
            К заявке добавлен
            {{
              subject === 'partnership'
                ? 'пример оформления White Label'
                : subject === 'product'
                  ? 'запрос по оборудованию'
                  : subject === 'equipment'
                    ? 'список оборудования'
                    : 'предварительный расчёт'
            }}
          </summary>
          <pre>{{ attachment }}</pre>
        </details>
        <div class="button-row">
          <button type="submit" class="button button-primary" :disabled="!email">
            Оставить заявку
          </button>
        </div>
        <p class="content-note">
          {{
            email
              ? 'Откроется письмо с вашей заявкой. Проверьте данные и отправьте его из своей почтовой программы.'
              : 'Приём заявок по почте будет доступен после подключения контактного ящика.'
          }}
        </p>
        <p class="status-note" role="status">
          {{
            prepared
              ? 'Чтобы оставить заявку, отправьте подготовленное письмо из своей почтовой программы.'
              : ''
          }}
        </p>
        <NuxtLink to="/privacy" class="privacy-link">Как используются ваши данные</NuxtLink>
      </form>
    </div>
  </div>
</template>
<script setup lang="ts">
import { useProjectQuote } from '~/composables/useProjectQuote';
import { useCart } from '~/core/composables/useCart';
definePageMeta({ path: '/contacts', alias: '/Contacts' });
const { email, phone, phoneHref, phoneDisplay } = useBusinessContact();
const route = useRoute();
const subject = computed(() => String(route.query.subject || ''));
const isPartnership = computed(() => subject.value === 'partnership');
const { products, totalPrice } = useCart();
const { text: estimateText } = useEstimate();
const { text: projectText } = useProjectQuote();
const name = ref('');
const contact = ref('');
const message = ref('');
const prepared = ref(false);
const attachment = computed(() =>
  isPartnership.value
    ? [
        'Сотрудничество · White Label Robotics Vision',
        `Название бренда: ${String(route.query.brand || 'Уточнить').slice(0, 32)}`,
        `Основной цвет: ${/^#[0-9a-f]{6}$/i.test(String(route.query.color || '')) ? route.query.color : 'Уточнить'}`,
        'Прошу обсудить условия сотрудничества, запуска и поддержки.',
      ].join('\n')
    : subject.value === 'project'
      ? projectText()
      : subject.value === 'product'
        ? `Подбор оборудования: ${String(route.query.product || '')}\nМодель: ${String(route.query.model || '')}\nПрошу уточнить цену, наличие и установку с Robotics Vision.`
        : subject.value === 'estimate'
          ? estimateText()
          : subject.value === 'equipment' && products.value.length
            ? [
                ...products.value.map(
                  (item) =>
                    `${item.product.title} × ${item.quantity} — ${formatPrice(item.product.price * item.quantity)}`,
                ),
                `Итого за оборудование: ${formatPrice(totalPrice.value)}`,
              ].join('\n')
            : '',
);
const request = computed(() =>
  [
    isPartnership.value
      ? 'Robotics Vision — заявка на сотрудничество White Label'
      : 'Robotics Vision — заявка на проект',
    '',
    `Имя: ${name.value || 'Не указано'}`,
    `Контакт: ${contact.value || 'Не указан'}`,
    '',
    message.value,
    '',
    attachment.value,
  ].join('\n'),
);
const mailHref = computed(
  () =>
    `mailto:${email.value}?subject=${encodeURIComponent(isPartnership.value ? 'Сотрудничество White Label Robotics Vision' : 'Проект видеонаблюдения Robotics Vision')}&body=${encodeURIComponent(request.value)}`,
);
function sendRequest() {
  if (!email.value) return;
  window.location.href = mailHref.value;
  prepared.value = true;
}
watch([name, contact, message], () => {
  prepared.value = false;
});
useSeoMeta({
  title: 'Контакты — Robotics Vision',
  description:
    'Видеонаблюдение для дома и бизнеса в Санкт-Петербурге и Ленинградской области. Оставьте заявку на ваш проект.',
});
</script>
